import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Database, ShieldCheck, RefreshCw, Loader2, Activity, Webhook, FileSpreadsheet, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  listExports,
  requestTenantExport,
  getExportDownloadUrl,
} from "@/lib/exports.functions";
import { getTrustOverview } from "@/lib/trust.functions";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/app/settings/data-ownership")({
  head: () => ({
    meta: [
      { title: "Data ownership — CairnOS" },
      { name: "description", content: "Export, backup, and own your operational data at any time." },
    ],
  }),
  component: DataOwnership,
});

function bytes(n: number | null | undefined): string {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued: "bg-muted text-muted-foreground",
    running: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    failed: "bg-destructive/10 text-destructive",
  };
  return <Badge variant="outline" className={map[status] ?? ""}>{status}</Badge>;
}

function DataOwnership() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listExports);
  const requestFn = useServerFn(requestTenantExport);
  const urlFn = useServerFn(getExportDownloadUrl);
  const trustFn = useServerFn(getTrustOverview);

  const { data: trust } = useQuery({
    queryKey: ["trust-overview", tenantId],
    enabled: !!tenantId,
    queryFn: () => trustFn({ data: { tenantId: tenantId! } }),
    refetchInterval: 30_000,
  });

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["exports", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, limit: 100 } }),
    refetchInterval: (q) => {
      const rows = (q.state.data as any)?.exports ?? [];
      return rows.some((r: any) => r.status === "queued" || r.status === "running") ? 5_000 : false;
    },
  });

  const requestMut = useMutation({
    mutationFn: () => requestFn({ data: { tenantId: tenantId!, monthsBack: 12 } }),
    onSuccess: () => {
      toast.success("Full tenant backup queued. It will appear below in ~1–2 min.");
      void qc.invalidateQueries({ queryKey: ["exports", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const download = async (exportId: string) => {
    try {
      const { url, filename } = await urlFn({ data: { exportId } });
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const rows = data?.exports ?? [];
  const tenantRows = rows.filter((r: any) => r.scope === "tenant");
  const lastCompleted = tenantRows.find((r: any) => r.status === "completed");
  const totalExports = rows.length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Data Ownership</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your operational data is yours. Export it, back it up, or take it elsewhere — anytime, no support ticket required.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2"><CardDescription>Last full backup</CardDescription></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">
                {lastCompleted ? formatDistanceToNow(new Date(lastCompleted.completed_at), { addSuffix: true }) : "Never"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {lastCompleted ? bytes(lastCompleted.byte_size) + " • " + (lastCompleted.row_count ?? 0).toLocaleString() + " rows" : "Generate one below"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Total exports (90d)</CardDescription></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{totalExports}</div>
              <div className="text-xs text-muted-foreground mt-1">All formats, all entities</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Data health</CardDescription></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" /> Healthy
              </div>
              <div className="text-xs text-muted-foreground mt-1">RLS active • audit log active</div>
            </CardContent>
          </Card>
        </div>

        {trust && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Database className="h-4 w-4" /> Your data inventory</CardTitle>
              <CardDescription>Live row counts across every entity scoped to this tenant.</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.entries(
                trust.inventory.reduce<Record<string, typeof trust.inventory>>((acc, row) => {
                  (acc[row.group] ||= []).push(row);
                  return acc;
                }, {}),
              ).map(([group, rows]) => (
                <div key={group} className="mb-4 last:mb-0">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{group}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {rows.map((r) => (
                      <div key={r.key} className="border rounded-md px-3 py-2 bg-card">
                        <div className="text-xs text-muted-foreground truncate">{r.label}</div>
                        <div className="text-lg font-semibold tabular-nums">{r.count.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {trust && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2"><CardDescription className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Audit activity</CardDescription></CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold tabular-nums">{trust.audit.last24.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mt-1">events in last 24h • {trust.audit.last30d.toLocaleString()} in 30d</div>
                {trust.audit.lastAt && (
                  <div className="text-xs text-muted-foreground mt-2 truncate">
                    Last: <span className="font-mono">{trust.audit.lastAction}</span> {formatDistanceToNow(new Date(trust.audit.lastAt), { addSuffix: true })}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription className="flex items-center gap-1.5"><Webhook className="h-3.5 w-3.5" /> Webhook deliveries (24h)</CardDescription></CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold tabular-nums flex items-baseline gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400">{trust.webhooks.ok24.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">ok</span>
                  <span className="text-destructive ml-2">{trust.webhooks.fail24.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">failed</span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {trust.webhooks.fail24 === 0 ? "All endpoints healthy" : "Investigate failed deliveries in Settings"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription className="flex items-center gap-1.5"><FileSpreadsheet className="h-3.5 w-3.5" /> Imports run</CardDescription></CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold tabular-nums">{trust.importsTotal.toLocaleString()}</div>
                <Link to="/app/imports" className="text-xs text-primary hover:underline mt-2 inline-block">Open imports →</Link>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Security posture</CardTitle>
            <CardDescription>Guarantees enforced at the database layer — not just the application.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 md:grid-cols-2 text-sm">
            {[
              ["Row-level security", "Every tenant table scoped by auth.uid() — cross-tenant reads impossible."],
              ["Tenant-scoped storage", "All uploads land under <tenant_id>/ with signed-URL access only."],
              ["Append-only audit log", "Every state-changing action recorded with actor, entity, and diff."],
              ["Encrypted at rest", "Postgres + object storage encrypted; backups retained per provider SLA."],
              ["Webhook signature", "Outbound events HMAC-signed; receivers can verify authenticity."],
              ["API keys are scoped", "Each key is hashed at rest, scoped to a tenant, and revocable."],
            ].map(([title, body]) => (
              <div key={title} className="flex gap-2 items-start">
                <ShieldCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">{title}</div>
                  <div className="text-xs text-muted-foreground">{body}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Database className="h-4 w-4" /> Full tenant backup</CardTitle>
            <CardDescription>
              ZIP archive of every entity (orders, SKUs, inventory, shipments, receipts, RMAs, customers, suppliers, locations) for the last 12 months. Includes a manifest.json.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => requestMut.mutate()}
              disabled={!tenantId || requestMut.isPending}
            >
              {requestMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
              Generate full backup
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Export History</CardTitle>
              <CardDescription>All exports across every list view and full-tenant backups.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => void refetch()} disabled={isRefetching}>
              <RefreshCw className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No exports yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Entity</th>
                      <th className="px-4 py-2 font-medium">Scope</th>
                      <th className="px-4 py-2 font-medium">Format</th>
                      <th className="px-4 py-2 font-medium">Rows</th>
                      <th className="px-4 py-2 font-medium">Size</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">When</th>
                      <th className="px-4 py-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r: any) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-2 font-mono text-xs">{r.entity}</td>
                        <td className="px-4 py-2 text-muted-foreground">{r.scope}</td>
                        <td className="px-4 py-2 uppercase text-xs">{r.format}</td>
                        <td className="px-4 py-2 tabular-nums">{r.row_count?.toLocaleString() ?? "—"}</td>
                        <td className="px-4 py-2 tabular-nums">{bytes(r.byte_size)}</td>
                        <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">
                          {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {r.status === "completed" && r.scope === "tenant" ? (
                            <Button size="sm" variant="outline" onClick={() => void download(r.id)}>
                              <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                            </Button>
                          ) : r.status === "failed" ? (
                            <span className="text-xs text-destructive" title={r.error ?? undefined}>Failed</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}